import React, { FC } from 'react';
import { MessageDialog } from 'src/components/widgets/dialog';
import { T } from 'src/shared/helpers';
import { SwitchWizardStep, SwitchWizardStepIndicator } from './switchWizardSteps';
import { TASKSTATUS } from './switchTypes';

const FOOTNOTE_STYLE = { marginTop: '20px', fontSize: '14px', color: '#666' } as const;

/**
 * Most recent completed EDITSUBSCRIPTION task, or null. Strict `>` keeps the earlier task on a taskID tie.
 * Generic over the task shape because the two call sites read it from selections with different field sets.
 */
export const findLastCompletedEditSubscriptionTask = <T extends { taskID: number; taskType: string; state: number }>(tasks: readonly T[]): T | null =>
    tasks
        .filter((task) => task.taskType === 'EDITSUBSCRIPTION' && task.state === TASKSTATUS.Completed)
        .reduce<T | null>((max, task) => (max === null || task.taskID > max.taskID ? task : max), null);

interface CompleteSwitchDialogProps {
    onSwitchProducts: () => void;
}

/**
 * Final confirmation once the seller has subscribed to the new plan. Shown from two separate effects — the
 * ?subscriptionComplete=true return and the isSellerMigrating page-refresh path — which stay separate because
 * they differ in trigger, guard ordering and how they clear the URL.
 */
const CompleteSwitchDialog: FC<CompleteSwitchDialogProps> = ({ onSwitchProducts }) => (
    <MessageDialog
        showCloseButton={false}
        titleHtml={<SwitchWizardStepIndicator step={SwitchWizardStep.SwitchProducts} />}
        message={
            <>
                <h3>{T._('Complete Sales Channel Switch', 'Admin')}</h3>
                <p>{T._('Thank you for subscribing to your new plan!', 'Admin')}</p>
                <p>{T._('You can now proceed to switch your products to the new sales channel.', 'Admin')}</p>
                <p>{T._('Click the button below to start the product migration process.', 'Admin')}</p>
                <p style={FOOTNOTE_STYLE}>{T._('This process will run in the background and you will be notified when it completes.', 'Admin')}</p>
            </>
        }
        buttons={[
            {
                label: T._('Switch Products', 'Admin'),
                onClick: onSwitchProducts
            }
        ]}
    />
);

export default CompleteSwitchDialog;
